<template>
  <div
    class="c-outreach-contact-twitter-posts"
    data-testid="outreach-contact-twitter-posts"
  >
    <div class="c-outreach-contact-twitter-posts__scroll">
      <QInfiniteScroll
        class="c-outreach-contact-twitter-posts__body"
        :scroll-target="
          scrollTarget || '.c-outreach-contact-twitter-posts__scroll'
        "
        @load="onLoad"
      >
        <OutreachContactTwitterPostCard
          v-for="tweet in tweets"
          :key="tweet.external_id"
          :engagement-url="engagementUrl"
          :tweet="tweet"
          :twitter-handle="twitterHandle"
        >
          <template #after>
            <OutreachContactTwitterPostCard
              v-for="relatedTweet in tweet.related_tweets"
              :key="relatedTweet.external_id"
              :engagement-url="engagementUrl"
              :tweet="relatedTweet"
              :twitter-handle="relatedTweet.user.username"
            />
          </template>
        </OutreachContactTwitterPostCard>

        <div
          v-if="loading"
          class="c-outreach-contact-twitter-posts__loader"
        >
          <QSpinnerDots size="35px" />
        </div>
      </QInfiniteScroll>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";

import OutreachContactTwitterPostCard from "./OutreachContactTwitterPostCard.vue";

const props = defineProps({
  socialIntegrationId: {
    type: String,
    required: true,
  },
  twitterUserId: {
    type: String,
    default: null,
  },
  twitterHandle: {
    type: String,
    default: null,
  },
  scrollTarget: {
    type: String,
    default: null,
  },
});

const loading = ref(true);
const tweets = ref([]);
const nextPageUrl = ref(null);

async function fetchTweets() {
  try {
    loading.value = true;

    const { data } = await $streemApiV1.get(
      "social_integrations/twitter/user_posts",
      {
        params: {
          social_integration_id: props.socialIntegrationId,
          twitter_user_id: props.twitterUserId,
          next_page_url: nextPageUrl.value,
        },
      }
    );

    tweets.value.push(...data.results);
    nextPageUrl.value = data.next_page;
  } catch (error) {
    ErrorDialog(
      getLocaleText("outreach_contact_twitter_posts.load_posts_error"),
      error
    );

    nextPageUrl.value = null;
  } finally {
    loading.value = false;
  }
}

async function onLoad(index, done) {
  if (!nextPageUrl.value || loading.value) {
    done();

    return;
  }

  await fetchTweets();
  done();
}

function engagementUrl(externalUrl, type = "") {
  return `https://twitter.com/${props.twitterHandle}/status/${externalUrl}/${type}`;
}

watch(() => props.twitterUserId, fetchTweets);
</script>

<style scoped lang="scss">
.c-outreach-contact-twitter-posts {
  display: flex;
  flex-direction: column;
  row-gap: 5px;
  flex: 1;
  overflow: hidden;

  &__scroll {
    flex: 1;
    overflow: auto;
  }

  &__body {
    display: flex;
    flex-direction: column;
    row-gap: 14px;
  }

  &__loader {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
